import type { AspectRatioValues, CropArea, ImageTransform } from '@/components/image-cropper/types'
import { DEFAULT_TRANSFORM } from '@/components/image-cropper/utils/image'
import type { CropperRef } from 'react-advanced-cropper'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

interface CropperState {
  currentTransform: ImageTransform
  isProcessing: boolean

  imageNaturalWidth: number
  imageNaturalHeight: number

  cropperRef: CropperRef | null

  initializeCrop: (crop: CropArea) => void
  setCrop: (crop: CropArea) => void
  setZoom: (zoom: number) => void
  setAspectRatio: (aspectRatio: AspectRatioValues) => void
  setRotation: (rotation: number) => void
  flipHorizontal: () => void
  flipVertical: () => void
  reset: (transform?: ImageTransform) => void
  setProcessing: (isProcessing: boolean) => void
  setImageDimensions: (width: number, height: number) => void
  setCropperRef: (ref: CropperRef | null) => void

  getCurrentCrop: () => CropArea
}

export const useCropperStore = create<CropperState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      currentTransform: DEFAULT_TRANSFORM,
      isProcessing: false,
      imageNaturalWidth: 0,
      imageNaturalHeight: 0,
      cropperRef: null,

      initializeCrop: (crop: CropArea) => {
        set((state) => {
          state.currentTransform.crop = crop
        })
      },

      setCrop: (crop: CropArea) => {
        set((state) => {
          state.currentTransform.crop = crop
        })
      },

      setZoom: (zoom: number) => {
        set((state) => {
          state.currentTransform.zoom = zoom
        })
      },

      setAspectRatio: (aspectRatio: AspectRatioValues) => {
        set((state) => {
          state.currentTransform.aspectRatio = aspectRatio
        })
      },

      setRotation: (rotation: number) => {
        set((state) => {
          state.currentTransform.rotation = rotation
        })
      },

      flipHorizontal: () => {
        set((state) => {
          state.currentTransform.flipHorizontal = !state.currentTransform.flipHorizontal
        })
      },

      flipVertical: () => {
        set((state) => {
          state.currentTransform.flipVertical = !state.currentTransform.flipVertical
        })
      },

      reset: (transform = DEFAULT_TRANSFORM) => {
        set((state) => {
          state.currentTransform = { ...transform }
          state.isProcessing = false
        })
      },

      setProcessing: (isProcessing: boolean) => {
        set((state) => {
          state.isProcessing = isProcessing
        })
      },

      setImageDimensions: (width: number, height: number) => {
        set((state) => {
          state.imageNaturalWidth = width
          state.imageNaturalHeight = height
        })
      },

      setCropperRef: (ref: CropperRef | null) => {
        set((state) => {
          state.cropperRef = ref
        })
      },

      getCurrentCrop: () => {
        const state = get()
        return state.currentTransform.crop
      },
    })),
  ),
)

export const useCropperTransform = () => useCropperStore((state) => state.currentTransform)
export const useCropperCrop = () => useCropperStore((state) => state.currentTransform.crop)
export const useCropperAspectRatio = () => useCropperStore((state) => state.currentTransform.aspectRatio)
